import { type ReactNode, useRef, useState } from "react";

import { type AriaDatePickerProps, type DateValue, useDatePicker } from "react-aria";
import { Dialog } from "react-aria-components";
import { useDatePickerState } from "react-stately";

import { Button } from "#components/Button";
import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { CalendarIcon } from "#icons";
import { cn, customTv, initializeYearOptions } from "#utils";

import { Calendar } from "./Calendar";
import { DateField } from "./DateField";
import { Popover } from "./Popover";

const triggerStyles = customTv({
  base: `
    flex items-center justify-between overflow-hidden rounded-1 outline-none border border-outline-input-enabled
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover px-4 py-2 gap-x-4
  `,
  variants: {
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
    isFocused: {
      true: "ring-1 !ring-outline-input-focus !border-outline-input-focus",
    },
    isDisabled: {
      true: "cursor-not-allowed [&_*]:!cursor-not-allowed border-outline-input-disabled bg-background-primary hover:!ring-0 hover:!border-outline-input-disabled",
    },
    isReadOnly: {
      true: "!bg-background-secondary !border-transparent hover:!ring-0",
    },
  },
});

export interface DatePickerProps<T extends DateValue> extends AriaDatePickerProps<T> {
  yearOptions?: number[];
  assistiveText?: ReactNode;
  tooltipText?: ReactNode;
  className?: string;
}

export function DatePicker<T extends DateValue>({
  yearOptions,
  assistiveText,
  tooltipText,
  className,
  ...props
}: DatePickerProps<T>) {
  const ref = useRef<HTMLDivElement>(null);

  const { years, minDate, maxDate } = initializeYearOptions(
    yearOptions,
    props.minValue,
    props.maxValue,
  );

  const state = useDatePickerState({
    ...props,
    minValue: minDate,
    maxValue: maxDate,
  });

  const { groupProps, labelProps, fieldProps, buttonProps, dialogProps, calendarProps } =
    useDatePicker(
      {
        ...props,
        minValue: minDate,
        maxValue: maxDate,
        shouldForceLeadingZeros: true,
      },
      state,
      ref,
    );

  const isInvalid = props.isInvalid || state.realtimeValidation.isInvalid;
  const isDisabled = props.isDisabled ?? false;
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={cn("relative flex flex-col gap-[.15rem]", className)}>
      <FormFieldHeader
        isDisabled={isDisabled}
        label={props.label}
        labelProps={labelProps}
        tooltipText={tooltipText}
      />
      <div
        className={triggerStyles({
          isFocused: !isDisabled && !props.isReadOnly && isFocused,
          isInvalid,
          isDisabled,
          isReadOnly: props.isReadOnly,
        })}
        {...groupProps}
        ref={ref}
      >
        <DateField focusInputContainer={setIsFocused} {...fieldProps} />
        <Button
          {...buttonProps}
          className={cn(
            "p-0",
            isDisabled ? "text-text-disabled" : "",
            props.isReadOnly ? "!text-text-primary" : "",
          )}
          isDisabled={isDisabled || props.isReadOnly}
          variant="plain"
        >
          <CalendarIcon className="size-lg" />
        </Button>
      </div>
      {assistiveText || state.realtimeValidation.validationErrors.length ? (
        <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
          {state.realtimeValidation.validationErrors.length
            ? state.realtimeValidation.validationErrors.join(", ")
            : assistiveText}
        </FormFieldAssistiveText>
      ) : null}

      {state.isOpen ? (
        <Popover placement="bottom" state={state} triggerRef={ref}>
          <Dialog
            {...dialogProps}
            className="rounded-md border border-outline-decorative bg-background-primary p-xl shadow-md"
          >
            <Calendar {...calendarProps} yearOptions={years} />
          </Dialog>
        </Popover>
      ) : null}
    </div>
  );
}
